import axios from 'axios';
import UrlFactory from './url_factory';
import { getAuthCookie } from '../utils/cookies';

class API extends UrlFactory {
    static _instance = null;

    static Instance() {
        if (!API._instance) API._instance = new API();
        return API._instance;
    }

    async makeHeaders() {
        const token = getAuthCookie();
        if (token === 'expired') return {};
        return { headers: { token: token } };
    }

    // POST: /api/user/register
    async requestRegisterUser(payload) {
        axios
            .post(this.createRegisterUrl(), payload)
            .then(function (response) {
                return response.data;
            })
            .catch(function (error) {
                if (error.response) return error.response.status;
            });
    }

    // POST: /api/user/authenticate
    async requestAuthenticate(payload) {
        const response = await axios.post(this.createAuthenticateUrl(), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/user
    async requestUser(token) {
        const response = await axios.get(this.createUserUrl(), await this.makeHeaders());
        return response;
    }

    // GET: /api/report/:aba
    async requestReport(aba, start, end, grouping) {
        const response = await axios.get(this.createReportUrl(aba, start, end, grouping), await this.makeHeaders());
        return response;
    }

    // GET: /api/consumer/fi/:aba
    async requestFI(aba) {
        const response = await axios.get(this.createFIUrl(aba), await this.makeHeaders());
        return response;
    }

    // GET: /api/users/fi/:aba
    async requestUsers(aba, filters = {}) {
        const params = {};

        // Add date filters if they exist
        if (filters.start) params.start = filters.start;
        if (filters.end) params.end = filters.end;

        // Add other filters
        if (filters.enrollment) params.enrollment = filters.enrollment;
        if (filters.hasCompleteDDSwitch) params.hasCompleteDDSwitch = filters.hasCompleteDDSwitch === 'complete';
        const response = await axios.get(this.createUsersUrl(aba), {
            ...(await this.makeHeaders()),
            params, // This will add the filters as query parameters
        });
        return response;
    }

    // GET: /api/admin/users/:aba
    async requestAdminUsers(aba) {
        const response = await axios.get(this.createAdminUsersUrl(aba), await this.makeHeaders());
        return response;
    }

    // GET: /api/consumer/ledger/:aba
    async requestLedger(aba) {
        const response = await axios.get(this.createLedgerUrl(aba), await this.makeHeaders());
        return response;
    }

    // POST: /api/consumer/fi/:aba
    async requestUpdateFI(aba, payload) {
        const response = await axios.post(this.createFIUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/report/ytd/:aba
    async requestYTDReport(aba) {
        const response = await axios.get(this.createYTDReportUrl(aba), await this.makeHeaders());
        return response;
    }

    // GET: /api/consumer/user/:clientId
    async requestConsumerUser(clientId) {
        const response = await axios.get(this.createConsumerUserUrl(clientId), await this.makeHeaders());
        return response;
    }

    // GET: /api/reconciliation/:aba
    async requestReconciliation(aba) {
        const response = await axios.get(this.createReconciliationUrl(aba), await this.makeHeaders());
        return response;
    }

    // GET: /api/treasury/summary/:aba
    async requestTreasurySummary(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createTreasurySummaryUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/funding/:aba
    async requestTreasuryFunding(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createTreasuryFundingUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/kpis/:aba
    async requestTreasuryKPIs(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createTreasuryKPIsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/receivables/:aba
    async requestTreasuryReceivables(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createTreasuryReceivablesUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/activities/:aba
    async requestTreasuryActivities(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createTreasuryActivitiesUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/treasury/cashflow/:aba
    async requestTreasuryCashflow(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createTreasuryCashflowUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // POST: /api/admin/users/:aba/:user_id/remove-access
    async requestRemoveUserAccess(aba, userId) {
        const response = await axios.post(this.createRemoveUserAccessUrl(aba, userId), {}, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/send-invitation
    async requestSendInvitation(payload) {
        const response = await axios.post(this.createSendInvitationUrl(), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/user/validate-invitation
    async requestValidateInvitation(token) {
        const response = await axios.get(this.createValidateInvitationUrl(token), await this.makeHeaders());
        return response;
    }

    // POST: /api/user/update-password
    async requestUpdatePassword(payload) {
        const response = await axios.post(this.createUpdatePasswordUrl(), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/user/validate-password-reset
    async requestValidatePasswordReset(token) {
        const response = await axios.get(this.createValidatePasswordResetUrl(token));
        return response;
    }

    // POST: /api/user/register
    async requestRegister(payload) {
        const response = await axios.post(this.createRegisterUrl(), payload);
        return response;
    }

    // PUT: /api/user/account
    async requestUpdateAccount(payload) {
        const response = await axios.put(this.createUpdateAccountUrl(), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/users/send-password-reset/:aba
    async requestSendPasswordReset(aba, payload) {
        const response = await axios.post(this.createSendPasswordResetUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/billing/kpis/:aba
    async requestBillingKPIs(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createBillingKPIsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // GET: /api/billing/statements/:aba
    async requestBillingStatements(aba, startDate, endDate) {
        const params = {};
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
        const response = await axios.get(this.createBillingStatementsUrl(aba), {
            ...(await this.makeHeaders()),
            params
        });
        return response;
    }

    // POST: /api/billing/export/:aba
    async requestExportBillingStatements(aba, payload) {
        const response = await axios.post(this.createExportBillingStatementsUrl(aba), payload, {
            ...(await this.makeHeaders()),
            responseType: 'blob'
        });
        return response;
    }

    // GET: /api/admin/sync-monitor/:aba
    async requestSyncMonitor(aba, status = null, limit = 50) {
        const response = await axios.get(this.createSyncMonitorUrl(aba, status, limit), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/sync-monitor/:aba/notify
    async requestSendSyncNotification(aba, payload) {
        const response = await axios.post(this.createSyncNotifyUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/trouble-ticket/:ticket_id/resolve
    async requestResolveTroubleTicket(ticketId, payload) {
        const response = await axios.post(this.createTroubleTicketResolveUrl(ticketId), payload, await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/platforms/:aba
    async requestPlatforms(aba, provider = null, search = null) {
        const response = await axios.get(this.createPlatformsUrl(aba, provider, search), await this.makeHeaders());
        return response;
    }

    // GET: /api/admin/platform-outages/:aba
    async requestPlatformOutages(aba, platformId = null, provider = null, status = 'active') {
        const response = await axios.get(this.createPlatformOutagesUrl(aba, platformId, provider, status), await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/platform-outage/:aba
    async requestCreatePlatformOutage(aba, payload) {
        const response = await axios.post(this.createPlatformOutageCreateUrl(aba), payload, await this.makeHeaders());
        return response;
    }

    // PUT: /api/admin/platform-outage/:aba/:outage_id
    async requestUpdatePlatformOutage(aba, outageId, payload) {
        const response = await axios.put(this.createPlatformOutageUpdateUrl(aba, outageId), payload, await this.makeHeaders());
        return response;
    }

    // POST: /api/admin/platform-outage/:aba/:outage_id/resolve
    async requestResolvePlatformOutage(aba, outageId) {
        const response = await axios.post(this.createPlatformOutageResolveUrl(aba, outageId), {}, await this.makeHeaders());
        return response;
    }
}

export default API;
